import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SidebarData2 as SidebarData } from "../../assets/menu/sideBarData";
import useTheme from "../../hooks/useTheme";
import awnLogo from "../../assets/images/awnLogo.svg";
import toggleIcon from "../../assets/images/toggle.svg";
import accountSettings from "../../assets/images/accountSettings.svg";
import { allRoutes } from "../../assets/routes/allRoutes";
import useRTLContext from "../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";

import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "./sidebar-new.scss";
import Dropdown from "react-bootstrap/Dropdown";
import PermissionContextFunctions from "../../assets/functions/PermissionContextFunctions";
import usePermissionContext from "../../hooks/usePermissionContext";
import * as Storage from "../../store/LocalStorage";
import useConfig from "../../hooks/useConfig";

const Sidebar = ({ toggleSidebar, isCollapsed, handleNoAccess }) => {
  const config = useConfig();
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const { grantPermission } = PermissionContextFunctions();
  const [sidebarData, setSidebarData] = useState([]);
  const Location = useLocation();
  const route = allRoutes;
  const { permissions } = usePermissionContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const icons = [
    "fa-solid fa-gauge-high",
    "fa-solid fa-users",
    "fa-solid fa-code-branch",
    "fa-solid fa-building",
    "fa-solid fa-cogs",
    "fa-solid fa-folder",
    "fa-solid fa-code-branch",
    "fa-solid fa-book",
    "fa-solid fa-user-tie",
    "fa-solid fa-bell",
  ];

  useEffect(() => {
    setLoading(true);
    try {
      filterSidebarData();
    } catch (err) {
      setError("Failed to initialize sidebar");
    } finally {
      setLoading(false);
    }
  }, [permissions, Storage?.get(config?.canAccessEmployeeKey)]);

  const filterSidebarData = () => {
    try {
      const canAccessEmployeeKey = Storage.get(config?.canAccessEmployeeKey);
      const hasCustomAddonsPermission = grantPermission([
        "Bank:read",

        "Religion:read",

        "JobTitle:read",
      ]);
      const filteredData = SidebarData.map((section) => {
        return {
          ...section,
          menuList: section.menuList.filter((item) => {
            if (item.label === "Custom Addons") {
              return hasCustomAddonsPermission;
            }

            return grantPermission([`${item.permissionKey}:read`]);

            // Otherwise use regular permission checking
          }),
        };
      }).filter((section) => section.menuList.length > 0);

      setSidebarData(filteredData);
      // Set no access state based on filtered data
      if (filteredData.length === 0) {
        handleNoAccess(true);
      } else {
        handleNoAccess(false);
      }
    } catch (error) {
      console.error("Error filtering sidebar data:", error);
      setError("Failed to load menu items");
    }
  };

  const LoadingState = () => (
    <div className="flex flex-col items-center justify-center text-center px-3 mt-5">
      <div className="w-12 h-12 border-4 border-gray-200 border-t-[#087a85] rounded-full animate-spin"></div>
      <p className="mt-3 text-sm font-medium text-[#475467]">
        {t("Loading menu items...")}
      </p>
    </div>
  );

  const ErrorState = () => (
    <div className="flex flex-col items-center justify-center text-center px-3 mt-5 space-y-3">
      <div className="text-red-500">
        <svg
          className="w-12 h-12 mx-auto"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <h5 className="text-lg font-bold text-[#04080b]">
        {t("Error Loading Menu")}
      </h5>
      <p className="text-sm font-medium text-[#475467] max-w-xs">
        {t(error || "Something went wrong while loading the menu items.")}
      </p>
      <button
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-[#087a85] bg-white border border-[#f2f4f7] rounded-lg hover:bg-[#f9fafb] focus:ring-2 focus:ring-[#087a85] focus:ring-offset-2 transition-colors duration-200"
        onClick={() => {
          setError(null);
          setLoading(true);
          try {
            filterSidebarData();
          } catch (err) {
            setError("Failed to reload menu");
          } finally {
            setLoading(false);
          }
        }}
      >
        <svg
          className="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        {t("Try Again")}
      </button>
    </div>
  );
  const convertToBasePath = (pathname) => {
    // Extract the first segment after splitting by "/"
    const formatted = pathname.split("/").slice(0, 2).join("/");
    return formatted;
  };

  let basePath = convertToBasePath(Location.pathname);

  return (
    <>
      <div className={`sidebar ${isCollapsed ? "small" : ""}`}>
        <div className="sidebar-logo-container ">
          <img src={awnLogo} alt={t("AWN Logo")} />
        </div>
        <div className="sidebar-menu-container">
          <img
            className="sidebar-toggle"
            src={toggleIcon}
            alt={t("Toggle Sidebar")}
            onClick={toggleSidebar}
          />
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState />
          ) : sidebarData && sidebarData.length > 0 ? (
            sidebarData.map((data, index) => (
              <div key={data.title} className="sidebar-menu-item">
                <span className="sidebar-menu-item-title">{t(data.title)}</span>
                {data?.menuList?.map((menuItem, index) => (
                  <Link
                    key={menuItem.link}
                    to={menuItem.link}
                    className={`sidebar-item-container ${
                      basePath === convertToBasePath(menuItem.link)
                        ? "active"
                        : ""
                    }`}
                  >
                    <img
                      src={
                        basePath === convertToBasePath(menuItem.link)
                          ? menuItem.iconActive
                          : menuItem.icon
                      }
                      className="sidebar-item-icon"
                      alt="nav icon"
                    />
                    <span className="sidebar-item-label">
                      {t(menuItem.label)}
                    </span>
                  </Link>
                ))}
              </div>
            ))
          ) : (
            <div className="no-access-wrapper d-flex flex-column align-items-center justify-content-center text-center px-3 mt-5">
              <img
                src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png" // or your own lock icon
                alt={t("No Access")}
                style={{ width: "60px", height: "60px", opacity: 0.6 }}
              />
              <h5 className="mt-3" style={{ color: "#888", fontWeight: 500 }}>
                {t("No Access")}
              </h5>
              <p style={{ fontSize: "14px", color: "#aaa" }}>
                {t(
                  "You currently don’t have permission to access any modules."
                )}
              </p>
            </div>
          )}
        </div>
        {/* <Link
          to={route.accountSettings}
          className="sidebar-setting"
          style={{ justifyContent: "center" }}
        >
          <img
            src={accountSettings}
            className="sidebar-setting-icon"
            alt={t("Account Settings Icon")}
          />
          <span className="sidebar-setting-label">
            {!isCollapsed ? t("Account Settings") : ""}
          </span>
        </Link> */}
      </div>
    </>
  );
};

export default Sidebar;
